/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from './supabase';
import HeadlineStrip from './components/costDashboard/HeadlineStrip';
import Watchlist from './components/costDashboard/Watchlist';
import VendorLeverage from './components/costDashboard/VendorLeverage';
import DistributorDrafts from './components/costDashboard/DistributorDrafts';
import TimeRangeControl from './components/costDashboard/TimeRangeControl';
import InvoiceReviewItemDrillIn from './components/InvoiceReviewItemDrillIn';
import { computeRangeDates } from './components/costDashboard/utils';

// Dashboard — operator's morning radar. Four sections after the
// restructure: HeadlineStrip, Watchlist (with polarity filter + row
// controls), VendorLeverage (default-collapsed), DistributorDrafts
// (default-collapsed; count chip shows ready drafts at a glance).
//
// Top Spend and Top Movers used to live here; they're now report types
// on the Invoices > Reports tab where operators expect to find rankings.
//
// Lives at INVENTORY · Dashboard (the top-of-group entry point operators
// hit first thing in the morning). The visual language matches the
// Invoice Review modal + Inventory Trends drawer.

export default function CostDashboard({ companyId }) {
  // ── Prefs ─────────────────────────────────────────────────────────────────
  // Time range default + last_seen come from cost_dashboard_prefs. We don't
  // gate render on the prefs query — show the 30d default immediately, then
  // patch in the saved range when it arrives. Operator never stares at a
  // spinner because of a one-row preference fetch.
  const [rangeKey, setRangeKey] = useState('30d');
  const [customRange, setCustomRange] = useState({ start: null, end: null });
  const [lastSeenAt, setLastSeenAt] = useState(null);
  const [highlightAlerts, setHighlightAlerts] = useState(false);
  const [drillIn, setDrillIn] = useState(null);
  const [watchlistAlertCount, setWatchlistAlertCount] = useState(0);
  // Watchlist polarity filter — persisted per user. Pinned items override
  // this filter (always visible), see Watchlist.js for the filter logic.
  const [watchlistPolarity, setWatchlistPolarity] = useState('all'); // 'all' | 'up' | 'down'
  const [operator, setOperator] = useState({ firstName: '', restaurantName: '' });
  const userIdRef = useRef(null);
  const sectionRefs = useRef({});

  // Pull prefs + company info on mount.
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      userIdRef.current = user.id;

      const [{ data: prefs }, { data: company }] = await Promise.all([
        supabase.from('cost_dashboard_prefs').select('*').eq('user_id', user.id).maybeSingle(),
        supabase.from('companies').select('manager_name').eq('id', companyId).maybeSingle(),
      ]);

      if (prefs) {
        setRangeKey(prefs.default_time_range || '30d');
        if (prefs.custom_range_start && prefs.custom_range_end) {
          setCustomRange({ start: prefs.custom_range_start, end: prefs.custom_range_end });
        }
        setLastSeenAt(prefs.last_seen_at);
        if (prefs.watchlist_polarity) setWatchlistPolarity(prefs.watchlist_polarity);
      }

      const fullName = company?.manager_name || user.user_metadata?.manager_name || '';
      const firstName = fullName.split(/\s+/)[0] || '';
      const restaurantName = user.user_metadata?.restaurant_name || '';
      setOperator({ firstName, restaurantName });
    })();
  }, [companyId]);

  // Bump last_seen_at 5s after the dashboard opens so the next visit's "NEW"
  // markers compute correctly. We capture the OLD value first (above) so this
  // visit's render still sees the prior value.
  useEffect(() => {
    if (!userIdRef.current) return;
    const t = setTimeout(async () => {
      await supabase.from('cost_dashboard_prefs').upsert({
        user_id: userIdRef.current,
        last_seen_at: new Date().toISOString(),
      });
    }, 5000);
    return () => clearTimeout(t);
  }, []);

  // Persist time-range changes.
  const persistRange = async (key, custom) => {
    if (!userIdRef.current) return;
    await supabase.from('cost_dashboard_prefs').upsert({
      user_id: userIdRef.current,
      default_time_range: key,
      custom_range_start: custom?.start || null,
      custom_range_end: custom?.end || null,
    });
  };

  const handleRangeChange = (key) => {
    setRangeKey(key);
    persistRange(key, customRange);
  };

  const handleCustomChange = (next) => {
    setCustomRange(next);
    if (rangeKey === 'custom') persistRange('custom', next);
  };

  const handleWatchlistPolarityChange = async (next) => {
    setWatchlistPolarity(next);
    if (!userIdRef.current) return;
    await supabase.from('cost_dashboard_prefs').upsert({
      user_id: userIdRef.current,
      watchlist_polarity: next,
    });
  };

  // ── Date math ─────────────────────────────────────────────────────────────
  const { start: startDate, end: endDate } = useMemo(
    () => computeRangeDates(rangeKey, customRange.start, customRange.end),
    [rangeKey, customRange.start, customRange.end]
  );

  // ── Drill-in plumbing ─────────────────────────────────────────────────────
  // Dashboard rows don't carry a current_invoice_id. When the operator
  // clicks a row, we fetch the latest invoice line for that food_item and
  // feed that into the drill-in panel — same shape as Invoice Review uses.
  const openDrillIn = async (row) => {
    const foodItemId = row.food_item_id;
    if (!foodItemId) return;
    const { data } = await supabase.from('invoice_line_items')
      .select('id, invoice_id, invoice_date, parse_diff')
      .eq('company_id', companyId)
      .eq('food_item_id', foodItemId)
      .order('invoice_date', { ascending: false })
      .limit(1);
    const latest = data?.[0];
    if (!latest) return;
    setDrillIn({
      foodItemId,
      itemName: row.item_name,
      isCatchWeight: !!row.is_catch_weight,
      currentInvoiceId: latest.invoice_id,
      currentInvoiceDate: latest.invoice_date,
      parseDiff: latest.parse_diff || null,
    });
  };

  // ── Headline-strip click targets ─────────────────────────────────────────
  const scrollToSection = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ESC closes the drill-in. The drill-in component itself doesn't register
  // a key handler — its parent (this component, in this surface; the
  // InvoiceReviewModal, in the original surface) is expected to.
  useEffect(() => {
    if (!drillIn) return;
    const onKey = (e) => { if (e.key === 'Escape') setDrillIn(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drillIn]);

  // Anomaly tile no longer has a TopMovers section to scroll to — Top
  // Movers moved to Invoices > Reports. Click the tile to jump to that
  // surface; the operator can run the report from there.
  const handleClickAnomalies = () => {
    navigateToReports();
  };
  const handleClickWatchlist = () => {
    if (watchlistAlertCount === 0) {
      scrollToSection('watchlist');
      return;
    }
    // Highlight the moved rows for 5s, then scroll.
    setHighlightAlerts(true);
    scrollToSection('watchlist');
    setTimeout(() => setHighlightAlerts(false), 5000);
  };

  // Cross-page navigate to Invoices > Reports tab. Persists the active
  // tab via cost_dashboard_prefs first so the destination loads on the
  // right tab without a flash.
  const navigateToReports = async () => {
    if (userIdRef.current) {
      await supabase.from('cost_dashboard_prefs').upsert({
        user_id: userIdRef.current,
        invoices_active_tab: 'reports',
      });
    }
    window.dispatchEvent(new CustomEvent('app:navigate', {
      detail: { page: 'inventory', tab: 'invoices' },
    }));
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
              Dashboard
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              Your morning radar — what to talk to your distributors about today.
            </p>
          </div>
          <TimeRangeControl
            value={rangeKey}
            onChange={handleRangeChange}
            customStart={customRange.start}
            customEnd={customRange.end}
            onCustomChange={handleCustomChange}
          />
        </div>

        <HeadlineStrip
          companyId={companyId}
          startDate={startDate}
          endDate={endDate}
          watchlistAlertCount={watchlistAlertCount}
          onClickAnomalies={handleClickAnomalies}
          onClickWatchlist={handleClickWatchlist}
        />

        <div ref={(el) => { sectionRefs.current.watchlist = el; }}>
          <Watchlist
            companyId={companyId}
            onOpenDrillIn={openDrillIn}
            onAlertCountChange={setWatchlistAlertCount}
            highlightAlerts={highlightAlerts}
            lastSeenAt={lastSeenAt}
            polarity={watchlistPolarity}
            onPolarityChange={handleWatchlistPolarityChange}
          />
        </div>

        <div ref={(el) => { sectionRefs.current.vendor = el; }}>
          <VendorLeverage
            companyId={companyId}
            startDate={startDate}
            endDate={endDate}
          />
        </div>

        <div ref={(el) => { sectionRefs.current.drafts = el; }}>
          <DistributorDrafts
            companyId={companyId}
            startDate={startDate}
            endDate={endDate}
            operatorFirstName={operator.firstName}
            restaurantName={operator.restaurantName}
          />
        </div>

        {/* Bottom spacer so the last section doesn't sit flush against the
            viewport bottom on tall monitors. */}
        <div style={{ height: 32 }} />
      </div>

      {/* Drill-in slide-over. Closes via X on the panel itself; ESC handled
          by the panel. Reuses the canonical drill-in component used by the
          Invoice Review modal. */}
      <InvoiceReviewItemDrillIn
        open={drillIn != null}
        onClose={() => setDrillIn(null)}
        companyId={companyId}
        foodItemId={drillIn?.foodItemId}
        itemName={drillIn?.itemName}
        itemCategory={null}
        isCatchWeight={drillIn?.isCatchWeight}
        currentInvoiceId={drillIn?.currentInvoiceId}
        currentInvoiceDate={drillIn?.currentInvoiceDate}
        parseDiff={drillIn?.parseDiff}
      />
    </div>
  );
}
